import { useEffect, useMemo, useRef } from 'react';
import { DAY_END, DAY_START, dailyPeaks } from '../lib/forecast';
import { TZ } from '../lib/openMeteo';
import { scoreColor } from '../lib/scoring';

export const fmtDay = (isoDate: string): string =>
  new Date(`${isoDate}T00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    timeZone: TZ,
  });

interface Props {
  times: string[];
  selIdx: number;
  /** index of the hour closest to now — powers the "Now" shortcut and the band tick */
  nowIdx: number;
  /** best score across all beaches, per hour of the forecast */
  best: (number | null)[];
  onSelectTime: (t: number) => void;
  onFindBest: () => void;
}

export default function TimeControls({
  times,
  selIdx,
  nowIdx,
  best,
  onSelectTime,
  onFindBest,
}: Props) {
  const days = useMemo(() => [...new Set(times.map((t) => t.slice(0, 10)))], [times]);
  const peaks = useMemo(() => dailyPeaks(times, best), [times, best]);
  const selDay = times[selIdx].slice(0, 10);
  const selHour = +times[selIdx].slice(11, 13);
  const dayStart = useMemo(() => times.findIndex((t) => t.startsWith(selDay)), [times, selDay]);

  /** the 24 hourly cells behind the scrubber, for the selected day */
  const band = useMemo(
    () => Array.from({ length: 24 }, (_, h) => best[dayStart + h] ?? null),
    [best, dayStart],
  );

  const stripRef = useRef<HTMLDivElement>(null);
  const selChipRef = useRef<HTMLButtonElement>(null);

  /* keep the active day chip in view when the day changes from elsewhere (e.g. "best window") */
  useEffect(() => {
    const chip = selChipRef.current;
    const strip = stripRef.current;
    if (!chip || !strip) return;
    const left = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [selDay]);

  /** keep the hour, move to the same hour on `day` */
  const goToDay = (day: string) => onSelectTime(times.findIndex((t) => t.startsWith(day)) + selHour);
  const isToday = selDay === times[nowIdx]?.slice(0, 10);

  return (
    <div id="timebox">
      <div id="daytabs" ref={stripRef} role="group" aria-label="Forecast day">
        {days.map((d, di) => {
          const active = d === selDay;
          const peak = peaks.get(d) ?? null;
          const label = di === 0 ? 'Today' : fmtDay(d);
          return (
            <button
              key={d}
              ref={active ? selChipRef : undefined}
              type="button"
              aria-pressed={active}
              aria-label={`${label}, best score ${peak == null ? 'unknown' : peak.toFixed(1)} out of 10`}
              className={active ? 'daychip active' : 'daychip'}
              onClick={() => goToDay(d)}
            >
              <span className="dlabel">{label}</span>
              <span className="dpeak" style={{ background: scoreColor(peak) }}>
                {peak == null ? '–' : peak.toFixed(1)}
              </span>
            </button>
          );
        })}
      </div>

      <div id="hourrow">
        <span id="hourlabel">
          {String(selHour).padStart(2, '0')}:00
          {selIdx === nowIdx && <span className="nowtag">now</span>}
        </span>
        <div className="hourbtns">
          <button
            type="button"
            className="minibtn"
            onClick={() => onSelectTime(nowIdx)}
            disabled={selIdx === nowIdx}
          >
            Now
          </button>
          <button type="button" className="minibtn accent" onClick={onFindBest}>
            ⚑ Best week
          </button>
        </div>
      </div>

      <div id="hourscrub">
        <div className="hourband" aria-hidden="true">
          {band.map((s, h) => (
            <span
              key={h}
              className={h < DAY_START || h > DAY_END ? 'hcell night' : 'hcell'}
              style={{ background: scoreColor(s) }}
            />
          ))}
          {isToday && nowIdx >= dayStart && nowIdx < dayStart + 24 && (
            <span className="nowtick" style={{ left: `${((nowIdx - dayStart + 0.5) / 24) * 100}%` }} />
          )}
        </div>
        <input
          type="range"
          id="hourslider"
          min={0}
          max={23}
          step={1}
          value={selHour}
          aria-label="Hour of day"
          aria-valuetext={`${String(selHour).padStart(2, '0')}:00`}
          onChange={(e) => onSelectTime(dayStart + +e.target.value)}
        />
      </div>
      <div className="scrubscale" aria-hidden="true">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>23</span>
      </div>
    </div>
  );
}
